import { useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLanguage } from '@/i18n/LanguageContext'
import { useSequences, useCreateSequence, useUpdateSequence, useDeleteSequence, useEnrollSequence, useToggleSequence, useBulkToggleSequences } from '@/api/hooks/useSequences'
import { Switch } from '@/components/ui/switch'
import type { SequenceListItem, SequenceStep as SeqStep } from '@/api/hooks/useSequences'
import { useAccounts } from '@/api/hooks/useAccounts'
import { useTemplates } from '@/api/hooks/useBroadcast'
import { ClickStatsPanel } from './ClickStatsPanel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/shared/EmptyState'
import { toast } from 'sonner'
import { Plus, Trash2, Clock, Users, Play, Instagram, Facebook, Timer, Link2, ExternalLink, ImageIcon, X, Loader2, Pencil, Mic, Power, PowerOff, Shuffle } from 'lucide-react'
import { api } from '@/api/client'
import { FolderPickerModal } from '@/components/FolderPickerModal'
import type { SequenceButton } from '@/api/hooks/useSequences'

function StepImageUpload({ imageUrl, onChange }: { imageUrl: string; onChange: (url: string) => void }) {
  const { t } = useLanguage()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [folderOpen, setFolderOpen] = useState(false)

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const data = await api.upload(file)
      const fullUrl = data.url.startsWith('http') ? data.url : `${window.location.origin}${data.url}`
      onChange(fullUrl)
      toast.success(t('Imagem enviada!'))
    } catch {
      toast.error(t('Erro ao enviar imagem'))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const isRandom = imageUrl.startsWith('__random__')

  return (
    <div className="mt-1.5 space-y-1.5">
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFile} className="hidden" />
      {isRandom ? (
        <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-2.5 py-2">
          <Shuffle className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="flex-1 text-[11px] font-medium text-primary">{t('Foto aleatória da pasta')}</span>
          <button type="button" onClick={() => onChange('')} className="text-destructive/70 hover:text-destructive">
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : imageUrl ? (
        <div className="relative inline-block">
          <img src={imageUrl} alt="Step" className="h-16 w-16 rounded-lg object-cover border border-border/40" />
          <button type="button" onClick={() => onChange('')} className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm">
            <X className="h-2.5 w-2.5" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex h-10 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border/40 text-[10px] text-muted-foreground hover:border-primary/40 hover:bg-primary/5 transition-all">
          {uploading ? <><Loader2 className="h-3 w-3 animate-spin" /> {t('Enviando...')}</> : <><ImageIcon className="h-3 w-3" /> {t('Imagem (opcional)')}</>}
        </button>
      )}
      {!isRandom && (
        <button type="button" onClick={() => setFolderOpen(true)} className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-primary/30 py-1.5 text-[10px] text-primary/80 hover:bg-primary/5 transition-all">
          <Shuffle className="h-3 w-3" /> {t('Atribuir pasta de mídias')}
        </button>
      )}
      <FolderPickerModal open={folderOpen} onOpenChange={setFolderOpen} onSelect={(id) => onChange('__random__:' + id)} />
    </div>
  )
}

function StepAudioUpload({ audioUrl, onChange }: { audioUrl: string; onChange: (url: string) => void }) {
  const { t } = useLanguage()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const data = await api.upload(file)
      const fullUrl = data.url.startsWith('http') ? data.url : `${window.location.origin}${data.url}`
      onChange(fullUrl)
      toast.success(t('Audio enviado!'))
    } catch {
      toast.error(t('Erro ao enviar audio'))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="mt-1.5">
      <input ref={fileRef} type="file" accept="audio/mpeg,audio/ogg,audio/mp4,audio/wav" onChange={handleFile} className="hidden" />
      {audioUrl ? (
        <div className="flex items-center gap-2 rounded-lg border border-border/40 bg-muted/10 px-3 py-2">
          <Mic className="h-3 w-3 text-primary shrink-0" />
          <span className="text-[10px] text-muted-foreground flex-1 truncate">{audioUrl.split('/').pop()}</span>
          <button type="button" onClick={() => onChange('')} className="flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm">
            <X className="h-2.5 w-2.5" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex h-10 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border/40 text-[10px] text-muted-foreground hover:border-primary/40 hover:bg-primary/5 transition-all">
          {uploading ? <><Loader2 className="h-3 w-3 animate-spin" /> {t('Enviando...')}</> : <><Mic className="h-3 w-3" /> {t('Audio (opcional)')}</>}
        </button>
      )}
    </div>
  )
}

export function Sequences() {
  const { t } = useLanguage()
  const { data: sequences, isLoading, refetch } = useSequences()
  const { data: seqStats } = useQuery<{ inQueue: number; totalSent: number; totalFailed: number; enrolled24h: number; lastSentAt: number | null; sequences: number }>({
    queryKey: ['sequence-stats'],
    queryFn: () => api.get('/sequences/stats'),
    refetchInterval: 20000,
  })
  const { data: accounts } = useAccounts()
  const { data: templates = [] } = useTemplates()
  const approvedTemplates = templates.filter((t) => t.approved > 0)
  const createSeq = useCreateSequence()
  const updateSeq = useUpdateSequence()
  const deleteSeq = useDeleteSequence()
  const enrollSeq = useEnrollSequence()
  const toggleSeq = useToggleSequence()
  const bulkToggle = useBulkToggleSequences()

  const [createOpen, setCreateOpen] = useState(false)
  const [editSeq, setEditSeq] = useState<SequenceListItem | null>(null)
  const [bulkMode, setBulkMode] = useState(false)
  const [bulkSaving, setBulkSaving] = useState(false)
  const [newAccountId, setNewAccountId] = useState('')
  const [newPlatform, setNewPlatform] = useState('fb')
  const [newName, setNewName] = useState('')
  // Template aprovado (máscara) que fura a janela de 24h. '' = não usa (só entrega dentro da janela).
  const [newTemplate, setNewTemplate] = useState('')
  const [newSteps, setNewSteps] = useState<Array<{ delayDays: number; message: string; imageUrl: string; audioUrl: string; buttons: SequenceButton[] }>>([{ delayDays: 0, message: '', imageUrl: '', audioUrl: '', buttons: [] }])

  const addStep = () => {
    if (newSteps.length >= 20) return
    setNewSteps([...newSteps, { delayDays: 25, message: '', imageUrl: '', audioUrl: '', buttons: [] }])
  }

  const removeStep = (i: number) => {
    if (newSteps.length <= 1) return
    setNewSteps(newSteps.filter((_, idx) => idx !== i))
  }

  const updateStep = (i: number, field: 'delayDays' | 'message' | 'imageUrl' | 'audioUrl', value: string | number) => {
    const updated = [...newSteps]
    updated[i] = { ...updated[i], [field]: value }
    setNewSteps(updated)
  }

  const addButton = (stepIdx: number) => {
    const updated = [...newSteps]
    if ((updated[stepIdx].buttons?.length || 0) >= 3) return
    updated[stepIdx] = { ...updated[stepIdx], buttons: [...(updated[stepIdx].buttons || []), { text: '', url: '' }] }
    setNewSteps(updated)
  }

  const updateButton = (stepIdx: number, btnIdx: number, field: 'text' | 'url' | 'type', value: string | undefined) => {
    const updated = [...newSteps]
    const buttons = [...(updated[stepIdx].buttons || [])]
    buttons[btnIdx] = { ...buttons[btnIdx], [field]: value }
    updated[stepIdx] = { ...updated[stepIdx], buttons }
    setNewSteps(updated)
  }

  // Alterna Link <-> Autoresposta. Precisa mexer em 'type' e 'url' de uma vez so:
  // updateButton le newSteps do closure, entao duas chamadas seguidas se atropelam.
  const toggleButtonType = (stepIdx: number, btnIdx: number) => {
    const updated = [...newSteps]
    const buttons = [...(updated[stepIdx].buttons || [])]
    const virandoReply = buttons[btnIdx].type !== 'reply'
    buttons[btnIdx] = {
      ...buttons[btnIdx],
      type: virandoReply ? 'reply' : undefined,
      url: virandoReply ? '' : buttons[btnIdx].url,
    }
    updated[stepIdx] = { ...updated[stepIdx], buttons }
    setNewSteps(updated)
  }

  const removeButton = (stepIdx: number, btnIdx: number) => {
    const updated = [...newSteps]
    updated[stepIdx] = { ...updated[stepIdx], buttons: updated[stepIdx].buttons.filter((_, idx) => idx !== btnIdx) }
    setNewSteps(updated)
  }

  const resetForm = () => {
    setNewName('')
    setNewAccountId('')
    setNewTemplate('')
    setNewSteps([{ delayDays: 0, message: '', imageUrl: '', audioUrl: '', buttons: [] }])
    setEditSeq(null)
  }

  const openBulk = () => {
    // Pré-popula com os steps de uma sequência existente (todas são iguais).
    const base = sequences?.[0]
    if (base) {
      setNewName(base.name || 'Novidades')
      setNewTemplate(base.steps?.[0]?.templateName || '')
      setNewSteps(base.steps.map(s => ({
        delayDays: s.delayDays, message: s.message,
        imageUrl: s.imageUrl || '', audioUrl: s.audioUrl || '', buttons: s.buttons || [],
      })))
    } else {
      setNewName('Novidades')
      setNewSteps([{ delayDays: 0, message: '', imageUrl: '', audioUrl: '', buttons: [] }])
    }
    setEditSeq(null)
    setBulkMode(true)
    setCreateOpen(true)
  }

  const openEdit = (seq: SequenceListItem) => {
    setEditSeq(seq)
    setNewAccountId(seq.accountId)
    setNewPlatform(seq.platform)
    setNewName(seq.name)
    setNewTemplate(seq.steps?.[0]?.templateName || '')
    setNewSteps(seq.steps.map(s => ({
      delayDays: s.delayDays,
      message: s.message,
      imageUrl: s.imageUrl || '',
      audioUrl: s.audioUrl || '',
      buttons: s.buttons || [],
    })))
    setCreateOpen(true)
  }

  const handleBulk = async () => {
    if (!newName.trim()) { toast.error(t('Digite um nome')); return }
    if (!newSteps[0]?.message?.trim() && !newSteps[0]?.cardTitle?.trim()) { toast.error(t('Adicione pelo menos um step')); return }
    setBulkSaving(true)
    try {
      const res: any = await api.post('/sequences/bulk', {
        platform: 'fb',
        name: newName.trim(),
        onlyExisting: false, // cria nas que não têm também
        steps: newSteps.filter(s => s.message?.trim() || (s as any).cardTitle?.trim()).map(s => ({ ...s, templateName: newTemplate || undefined })),
      })
      toast.success(`${t('Aplicado em')} ${res.applied} ${t('páginas')}!`)
      setCreateOpen(false); setBulkMode(false); resetForm()
      refetch?.()
    } catch (e: any) {
      toast.error(e?.message || t('Erro ao aplicar'))
    } finally { setBulkSaving(false) }
  }

  const handleCreate = () => {
    if (!editSeq && !newAccountId) { toast.error(t('Selecione uma conta')); return }
    if (!newName.trim()) { toast.error(t('Digite um nome')); return }
    if (!newSteps[0]?.message.trim()) { toast.error(t('Adicione pelo menos uma mensagem')); return }

    if (editSeq) {
      updateSeq.mutate(
        {
          sequenceId: editSeq.id,
          accountId: editSeq.accountId,
          platform: editSeq.platform,
          name: newName.trim(),
          steps: newSteps.filter(s => s.message.trim()).map(s => ({ ...s, templateName: newTemplate || undefined })),
        },
        {
          onSuccess: () => {
            toast.success(t('Sequencia atualizada!'))
            setCreateOpen(false)
            setEditSeq(null)
            resetForm()
          },
          onError: () => toast.error(t('Erro ao atualizar')),
        },
      )
      return
    }

    createSeq.mutate(
      {
        accountId: newAccountId,
        platform: newPlatform,
        name: newName.trim(),
        steps: newSteps.filter(s => s.message.trim()).map(s => ({ ...s, templateName: newTemplate || undefined })),
      },
      {
        onSuccess: () => {
          toast.success(t('Sequencia criada!'))
          setCreateOpen(false)
          resetForm()
        },
        onError: () => toast.error(t('Erro ao criar')),
      },
    )
  }

  const handleDelete = (id: string, accountId: string, platform: string, name: string) => {
    if (!confirm(`${t('Excluir')} "${name}"?`)) return
    deleteSeq.mutate({ sequenceId: id, accountId, platform }, {
      onSuccess: () => toast.success(t('Sequencia excluida!')),
      onError: () => toast.error(t('Erro ao excluir')),
    })
  }

  const handleEnroll = (id: string, accountId: string, platform: string, name: string) => {
    if (!confirm(`${t('Inscrever contatos')} "${name}"?`)) return
    enrollSeq.mutate({ sequenceId: id, accountId, platform }, {
      onSuccess: (data) => toast.success(`${data.enrolled} ${t('de')} ${data.total} ${t('contatos inscritos!')}`),
      onError: () => toast.error(t('Erro ao inscrever')),
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-6 px-6 md:px-8 py-6">
        <Skeleton className="h-6 w-48" />
        <div className="grid gap-4 sm:grid-cols-2">
          {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-48 rounded-xl" />)}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 px-6 md:px-8 py-6">
      <div className="pb-4">
        <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">{t('Sequencias')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('Drip campaigns - mensagens agendadas com delay em minutos')}</p>
        </div>
        <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          disabled={bulkToggle.isPending}
          onClick={() => bulkToggle.mutate({ isActive: true })}
          title={t('Liga todas as sequencias de uma vez')}
        >
          <Power className="h-3.5 w-3.5" /> {t('Ativar todas')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 text-amber-600 hover:text-amber-700"
          disabled={bulkToggle.isPending}
          onClick={() => {
            if (confirm(t('Pausar TODAS as sequencias? Os leads ficam parados onde estao e retomam quando reativar.'))) {
              bulkToggle.mutate({ isActive: false })
            }
          }}
          title={t('Pausa todas - nada e apagado')}
        >
          <PowerOff className="h-3.5 w-3.5" /> {t('Pausar todas')}
        </Button>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={openBulk}>
          <Users className="h-3.5 w-3.5" /> {t('Editar todas')}
        </Button>
        <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) { setBulkMode(false); resetForm() } }}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5" onClick={() => { resetForm(); setBulkMode(false); setEditSeq(null) }}><Plus className="h-3.5 w-3.5" /> {t('Nova Sequencia')}</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{bulkMode ? t('Editar TODAS as páginas') : (editSeq ? t('Editar Sequencia') : t('Nova Sequencia'))}</DialogTitle>
            </DialogHeader>
            {bulkMode && <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-2 text-xs text-amber-600">{t('Estes steps serão aplicados a TODAS as páginas FB de uma vez.')}</div>}
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="mb-1.5 text-xs text-muted-foreground">{t('Conta')}</Label>
                  <Select value={newAccountId} onValueChange={setNewAccountId}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder={t('Selecione...')} /></SelectTrigger>
                    <SelectContent>
                      {accounts?.map((a) => (
                        <SelectItem key={a.igUserId} value={a.igUserId}>
                          {a.fbOnly ? a.name : `@${a.username}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 text-xs text-muted-foreground">{t('Plataforma')}</Label>
                  <Select value={newPlatform} onValueChange={setNewPlatform}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ig">Instagram</SelectItem>
                      <SelectItem value="fb">Facebook</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 text-xs text-muted-foreground">{t('Nome')}</Label>
                  <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ex: Onboarding" className="h-9 text-xs" />
                </div>
              </div>

              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                <Label className="mb-1.5 block text-xs font-medium text-primary">{t('Template aprovado (alcança leads fora das 24h)')}</Label>
                <Select value={newTemplate || '__none__'} onValueChange={(v) => setNewTemplate(v === '__none__' ? '' : v)}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('Nenhum — só dentro da janela de 24h')}</SelectItem>
                    {approvedTemplates.map((tpl) => (
                      <SelectItem key={tpl.id} value={tpl.name}>{tpl.name} · {tpl.approved} {t('págs')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1.5 text-[10px] text-muted-foreground">
                  {newTemplate
                    ? t('Fora das 24h, cada passo é entregue dentro da máscara aprovada — o texto do passo entra na variável. Coloque emoji na máscara (no template), não no texto do passo.')
                    : t('Sem template, os passos fora da janela de 24h NÃO são entregues (só alcança quem interagiu nas últimas 24h).')}
                </p>
                {approvedTemplates.length === 0 && (
                  <p className="mt-1 text-[10px] text-amber-500">{t('Nenhum template aprovado ainda. Crie um em Broadcast → Templates (máscara neutra + variável).')}</p>
                )}
              </div>

              <div>
                <Label className="mb-2 text-xs text-muted-foreground">{t('Passos (max 20)')}</Label>
                <div className="space-y-2">
                  {newSteps.map((step, i) => (
                    <div key={i} className="rounded-lg border border-border bg-card/50 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-400/10 text-[10px] font-bold text-blue-600 dark:text-blue-400">
                            {i + 1}
                          </div>
                          <span className="text-[10px] text-muted-foreground">{t('Passo')} {i + 1}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 rounded border border-border px-2 py-0.5">
                            <Timer className="h-2.5 w-2.5 text-muted-foreground" />
                            <Input
                              type="number"
                              min={0}
                              max={30}
                              value={step.delayDays}
                              onChange={(e) => updateStep(i, 'delayDays', parseInt(e.target.value) || 0)}
                              className="h-5 w-10 border-0 bg-transparent p-0 text-center text-[10px]"
                            />
                            <span className="text-[10px] text-muted-foreground">{t('min')}</span>
                          </div>
                          {newSteps.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" className="h-5 w-5" onClick={() => removeStep(i)}>
                              <Trash2 className="h-2.5 w-2.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <Textarea
                        value={step.message}
                        onChange={(e) => updateStep(i, 'message', e.target.value)}
                        placeholder="Mensagem..."
                        className="min-h-[60px] resize-none text-xs"
                      />

                      {/* Image upload */}
                      <StepImageUpload
                        imageUrl={step.imageUrl || ''}
                        onChange={(url) => updateStep(i, 'imageUrl', url)}
                      />

                      {/* Audio upload */}
                      <StepAudioUpload
                        audioUrl={step.audioUrl || ''}
                        onChange={(url) => updateStep(i, 'audioUrl', url)}
                      />

                      {/* Buttons/Links */}
                      {step.buttons?.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {step.buttons.map((btn, bIdx) => (
                            <div key={bIdx} className="flex items-center gap-1.5">
                              <div className="flex flex-1 items-center gap-1 rounded border border-border bg-background px-2 py-1">
                                <Link2 className="h-3 w-3 shrink-0 text-muted-foreground" />
                                <Input
                                  value={btn.text}
                                  onChange={(e) => updateButton(i, bIdx, 'text', e.target.value)}
                                  placeholder={t('Texto do botao')}
                                  className="h-6 border-0 bg-transparent p-0 text-[10px] shadow-none focus-visible:ring-0"
                                />
                              </div>
                              {btn.type === 'reply' ? (
                                <div className="flex flex-1 items-center rounded border border-dashed border-emerald-500/30 bg-emerald-500/5 px-2 py-1">
                                  <span className="text-[10px] text-emerald-600/80">
                                    {t('Gera mensagem no chat - reabre a janela de 24h')}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex flex-1 items-center gap-1 rounded border border-border bg-background px-2 py-1">
                                  <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                                  <Input
                                    value={btn.url}
                                    onChange={(e) => updateButton(i, bIdx, 'url', e.target.value)}
                                    placeholder="https://..."
                                    className="h-6 border-0 bg-transparent p-0 text-[10px] shadow-none focus-visible:ring-0"
                                  />
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => toggleButtonType(i, bIdx)}
                                className={`h-6 shrink-0 rounded border px-1.5 text-[9px] font-medium transition-colors ${
                                  btn.type === 'reply'
                                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600'
                                    : 'border-border bg-background text-muted-foreground hover:text-foreground'
                                }`}
                                title={btn.type === 'reply'
                                  ? t('Autoresposta: o texto vira mensagem da pessoa e REABRE a janela de 24h')
                                  : t('Link: leva a pessoa pro site, NAO reabre a janela')}
                              >
                                {btn.type === 'reply' ? t('Autoresposta') : t('Link')}
                              </button>
                              <Button type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => removeButton(i, bIdx)}>
                                <Trash2 className="h-2.5 w-2.5 text-muted-foreground" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {(step.buttons?.length || 0) < 3 && (
                        <button
                          type="button"
                          onClick={() => addButton(i)}
                          className="mt-1.5 flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors"
                        >
                          <Plus className="h-2.5 w-2.5" /> {t('Adicionar botao/link')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {newSteps.length < 20 && (
                  <Button type="button" variant="outline" size="sm" className="mt-2 w-full border-dashed text-[10px]" onClick={addStep}>
                    <Plus className="mr-1 h-2.5 w-2.5" /> {t('Adicionar passo')}
                  </Button>
                )}
              </div>

              <Button onClick={bulkMode ? handleBulk : handleCreate} disabled={createSeq.isPending || bulkSaving} className="w-full">
                {bulkSaving ? t('Aplicando a todas...') : (createSeq.isPending || updateSeq.isPending) ? t('Salvando...') : bulkMode ? t('Aplicar a TODAS as páginas') : editSeq ? t('Salvar Alteracoes') : t('Criar Sequencia')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
        </div>
      </div>

      <ClickStatsPanel />


      {/* Banner de saúde dos fluxos */}
      {seqStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-xl border bg-card p-3">
            <div className="text-[11px] text-muted-foreground">{t('Enviadas (total)')}</div>
            <div className="mt-0.5 text-xl font-bold text-emerald-500">{seqStats.totalSent.toLocaleString()}</div>
          </div>
          <div className="rounded-xl border bg-card p-3">
            <div className="text-[11px] text-muted-foreground">{t('Na fila agora')}</div>
            <div className="mt-0.5 text-xl font-bold">{seqStats.inQueue.toLocaleString()}</div>
          </div>
          <div className="rounded-xl border bg-card p-3">
            <div className="text-[11px] text-muted-foreground">{t('Entraram (24h)')}</div>
            <div className="mt-0.5 text-xl font-bold text-primary">+{seqStats.enrolled24h.toLocaleString()}</div>
          </div>
          <div className="rounded-xl border bg-card p-3">
            <div className="text-[11px] text-muted-foreground">{t('Status')}</div>
            <div className="mt-0.5 flex items-center gap-1.5 text-sm font-semibold">
              {seqStats.lastSentAt && (Date.now() - seqStats.lastSentAt) < 30 * 60 * 1000
                ? <><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> {t('Ativo')}</>
                : seqStats.lastSentAt
                  ? <><span className="h-2 w-2 rounded-full bg-amber-500" /> {t('Último envio')}: {new Date(seqStats.lastSentAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</>
                  : <><span className="h-2 w-2 rounded-full bg-muted-foreground" /> {t('Sem envios ainda')}</>}
            </div>
          </div>
        </div>
      )}

      {!sequences?.length ? (
        <EmptyState
          icon={Clock}
          title={t('Nenhuma sequencia')}
          description={t('Crie campanhas drip com mensagens agendadas por minutos de delay.')}
          action={{ label: t('Criar Sequencia'), onClick: () => setCreateOpen(true) }}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {sequences.map((seq) => (
            <div key={`${seq.id}-${seq.platform}`} className={`group overflow-hidden rounded-xl border bg-card transition-all hover:border-primary/20 ${seq.isActive === false ? 'border-border/40 opacity-60' : 'border-border'}`}>
              <div className="px-4 py-3">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="rounded-lg bg-blue-400/10 p-1.5">
                      <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{seq.name}</div>
                      <div className="text-[10px] text-muted-foreground">{seq.accountName}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="gap-1 text-[10px]">
                      {seq.platform === 'ig' ? <Instagram className="h-2.5 w-2.5" /> : <Facebook className="h-2.5 w-2.5" />}
                      {seq.platform === 'ig' ? 'IG' : 'FB'}
                    </Badge>
                    {seq.steps?.[0]?.templateName && (
                      <Badge variant="outline" className="gap-1 border-primary/40 text-[10px] text-primary" title={`${t('Usa template aprovado')}: ${seq.steps[0].templateName}`}>
                        <Clock className="h-2.5 w-2.5" /> {t('fura 24h')}
                      </Badge>
                    )}
                    <div className="flex items-center gap-1.5" title={seq.isActive === false ? t('Sequencia pausada - nenhuma etapa e enviada') : t('Sequencia ativa')}>
                      <span className="text-[10px] text-muted-foreground">
                        {seq.isActive === false ? t('OFF') : t('ON')}
                      </span>
                      <Switch
                        checked={seq.isActive !== false}
                        onCheckedChange={(v) => toggleSeq.mutate({ id: seq.id, isActive: v })}
                        disabled={toggleSeq.isPending}
                      />
                    </div>
                  </div>
                </div>

                {/* Timeline steps */}
                <div className="relative space-y-2">
                  {seq.steps.length > 1 && (
                    <div className="absolute left-[7px] top-3 bottom-3 w-px bg-border" />
                  )}
                  {seq.steps.map((step, i) => (
                    <div key={i} className="relative flex gap-3">
                      <div className="relative z-10 mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-blue-400/30 bg-card text-[8px] font-bold text-blue-600 dark:text-blue-400">
                        {i + 1}
                      </div>
                      <div className="flex-1 rounded-lg bg-muted/30 px-2.5 py-1.5">
                        <div className="mb-0.5 flex items-center gap-1.5">
                          <Timer className="h-2.5 w-2.5 text-muted-foreground" />
                          <span className="text-[9px] font-medium text-muted-foreground">
                            {step.delayDays === 0 ? t('Imediato') : `${step.delayDays} min`}
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground line-clamp-2">{step.message}</p>
                        {step.buttons && step.buttons.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {step.buttons.map((btn, bIdx) => (
                              <span key={bIdx} className="inline-flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary">
                                <Link2 className="h-2 w-2" /> {btn.text}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex border-t border-border/50">
                <button
                  onClick={() => openEdit(seq)}
                  className="flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                >
                  <Pencil className="h-3 w-3" /> {t('Editar')}
                </button>
                <div className="w-px bg-border/50" />
                <button
                  onClick={() => handleEnroll(seq.id, seq.accountId, seq.platform, seq.name)}
                  className="flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                >
                  <Users className="h-3 w-3" /> {t('Inscrever contatos')}
                </button>
                <div className="w-px bg-border/50" />
                <button
                  onClick={() => handleDelete(seq.id, seq.accountId, seq.platform, seq.name)}
                  className="flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-3 w-3" /> {t('Excluir')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
